/**
 * Building the OverlayDrawContext of a frame (W2-02; contract render/types.ts): coordinate mappings for a viewport in
 * the overlay canvas' device pixels, and a private copy of the engine's line states (the engine's LineStateRt
 * objects are pooled and overwritten by the next query of ANY caller, e.g. a column header readout, while pointer
 * handlers use the context between frames).
 */

import type { Engine, FrameDrawList, LineStateRt, PreviewFilters, Viewport } from '../../../core/engine/types';
import type { Mat2D, RGB } from '../../../core/util/types';
import type { Sec } from '../../../core/time/types';
import type { OverlayDrawContext } from '../../../render/types';

export interface OverlayContextInput {
  readonly ctx: CanvasRenderingContext2D;
  /** The chart viewport in the overlay canvas' device px. */
  readonly viewport: Viewport;
  readonly t: Sec;
  readonly list: FrameDrawList;
  readonly lines: readonly LineStateRt[];
  readonly engine: Engine;
  readonly dpr: number;
}

export function makeOverlayContext(input: OverlayContextInput): OverlayDrawContext {
  const { vx, vy, W, H } = input.viewport;
  const half = W / 2;
  return {
    ...input,
    worldToPx: (x, y) => [vx + (x + 1) * half, vy + H / 2 - y * half],
    pxToWorld: (px, py) => [half === 0 ? 0 : (px - vx) / half - 1, half === 0 ? 0 : (vy + H / 2 - py) / half],
    uiToPx: (x, y) => [vx + (x + 1) * half, vy + H / 2 + y * half],
  };
}

/** Mutable copy of a line state (same shape as LineStateRt). */
type LineCopy = { -readonly [K in keyof LineStateRt]: LineStateRt[K] };

function copyInto(out: LineCopy, s: LineStateRt): void {
  out.line = s.line;
  out.index = s.index;
  out.kind = s.kind;
  out.uiMover = s.uiMover;
  out.alpha = s.alpha;
  out.ownX = s.ownX;
  out.ownY = s.ownY;
  out.ownRot = s.ownRot;
  out.x = s.x;
  out.y = s.y;
  out.rot = s.rot;
  out.sx = s.sx;
  out.sy = s.sy;
  out.scaleYRaw = s.scaleYRaw;
  out.color = s.color === null ? null : ([s.color[0], s.color[1], s.color[2]] as RGB);
  out.text = s.text;
  out.height = s.height;
  out.inclineSin = s.inclineSin;
  out.gifProgress = s.gifProgress;
  out.paint = s.paint;
  out.drawsNothing = s.drawsNothing;
  out.m = [s.m[0], s.m[1], s.m[2], s.m[3], s.m[4], s.m[5]] as Mat2D;
}

/** A pool of line-state copies reused frame after frame. */
export class LineStatePool {
  private readonly items: LineCopy[] = [];
  private readonly out: LineStateRt[] = [];

  /** Copies `states` into pooled objects; the returned array is reused by the next call. */
  copy(states: readonly LineStateRt[]): readonly LineStateRt[] {
    this.out.length = 0;
    for (let i = 0; i < states.length; i++) {
      const s = states[i];
      if (s === undefined) continue;
      let item = this.items[i];
      if (item === undefined) {
        item = { ...s, m: [...s.m] as Mat2D };
        this.items.push(item);
      }
      copyInto(item, s);
      this.out.push(item);
    }
    return this.out;
  }
}

/** Engine line states at an aspect ratio (Engine.lineStates' `ar`). */
export function lineStatesAt(engine: Engine, t: Sec, filters: PreviewFilters, ar: number): readonly LineStateRt[] {
  return engine.lineStates(t, filters, ar);
}

/** One line's state at an aspect ratio, copied (safe to keep). */
export function lineStateCopy(
  engine: Engine,
  line: LineStateRt['line'],
  t: Sec,
  filters: PreviewFilters,
  ar: number,
): LineStateRt {
  const s = engine.lineState(line, t, filters, ar);
  const out = { ...s, m: [...s.m] as Mat2D } as LineCopy;
  copyInto(out, s);
  return out;
}
